import React from "react";
import { formatAssignmentStatus } from "../../utils/formatAssignmentStatus";
import { formatSpan } from "../../utils/ganttModel";

// Turns buildGanttModel() stage rows into GanttView rows. Shared by the
// single-project chart and the expanded projects in the portfolio chart.

export const STATUS_COLORS = {
  DONE: "#22c55e",
  IN_PROGRESS: "#3b82f6",
  SUBMITTED: "#8b5cf6",
  PENDING: "#f59e0b",
  REJECTED: "#f97316",
  RETURNED: "#ea580c",
  REASSIGNED: "#94a3b8",
  SKIPPED: "#cbd5e1",
};

export const STATE_LABELS = {
  DONE: "Done",
  ACTIVE: "Active",
  BLOCKED: "Blocked",
  UPCOMING: "Upcoming",
  SKIPPED: "Skipped",
  VISITED: "Visited",
};

export const STAGE_LEGEND = [
  { label: "Done", color: STATUS_COLORS.DONE },
  { label: "In Progress", color: STATUS_COLORS.IN_PROGRESS },
  { label: "Submitted", color: STATUS_COLORS.SUBMITTED },
  { label: "Not Started", color: STATUS_COLORS.PENDING },
  { label: "Returned / Rework", color: STATUS_COLORS.RETURNED },
  { label: "Projected", className: "legend-projected" },
  { label: "Overdue", className: "legend-overdue" },
  { label: "Weekly off", className: "legend-off" },
  { label: "Holiday", className: "legend-holiday" },
];

export function formatDateTime(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StageBarTooltip({ bar, row, projectName }) {
  const context = projectName && (
    <div className="gantt-tip-context">{projectName}</div>
  );

  if (bar.kind === "projected") {
    return (
      <>
        {context}
        <div className="gantt-tip-title">{row.stageName}</div>
        <div className="gantt-tip-badge gantt-tip-badge-projected">
          Projected · {STATE_LABELS[bar.status] || "Not started"}
        </div>
        <dl>
          <dt>Projected start</dt>
          <dd>{formatDateTime(bar.start)}</dd>
          <dt>Projected end</dt>
          <dd>{formatDateTime(bar.end)}</dd>
          {row.estimate && (
            <>
              <dt>Estimate</dt>
              <dd>{row.estimate}</dd>
            </>
          )}
        </dl>
        <div className="gantt-tip-note">
          {bar.status === "ACTIVE"
            ? "Stage is active but nobody is assigned yet."
            : "Based on the workflow estimate and working days, after the stages before it."}
        </div>
      </>
    );
  }

  const status = formatAssignmentStatus(bar.status);
  return (
    <>
      {context}
      <div className="gantt-tip-title">
        {row.stageName}
        {bar.visitNumber > 1 && (
          <span className="gantt-tip-visit"> · Visit {bar.visitNumber}</span>
        )}
      </div>
      <div
        className="gantt-tip-badge"
        style={{ background: status.bgColor, color: status.textColor }}
      >
        {status.label}
        {bar.scheduledFuture && " · Scheduled"}
      </div>
      <dl>
        <dt>Assignee</dt>
        <dd>{bar.assignee}</dd>
        <dt>{bar.scheduledFuture ? "Scheduled start" : "Started"}</dt>
        <dd>{formatDateTime(bar.start)}</dd>
        {bar.completedAt && (
          <>
            <dt>Finished</dt>
            <dd>{formatDateTime(bar.completedAt)}</dd>
            <dt>Took</dt>
            <dd>{formatSpan(bar.completedAt - bar.start)}</dd>
          </>
        )}
        {bar.expectedEnd && (
          <>
            <dt>Expected end</dt>
            <dd>{formatDateTime(bar.expectedEnd)}</dd>
          </>
        )}
        {row.estimate && (
          <>
            <dt>Estimate</dt>
            <dd>{row.estimate}</dd>
          </>
        )}
        {bar.timeLoggedMinutes > 0 && (
          <>
            <dt>Time logged</dt>
            <dd>{formatSpan(bar.timeLoggedMinutes * 60000)}</dd>
          </>
        )}
      </dl>
      {bar.overdueMs > 0 && (
        <div className="gantt-tip-alert">
          Overdue by {formatSpan(bar.overdueMs)}
        </div>
      )}
      {bar.lateFinishMs > 0 && (
        <div className="gantt-tip-alert">
          Finished {formatSpan(bar.lateFinishMs)} after the expected end
        </div>
      )}
      {(bar.reviewNote || bar.reassignmentReason) && (
        <div className="gantt-tip-note">
          {bar.reviewNote || bar.reassignmentReason}
        </div>
      )}
    </>
  );
}

function StageLabel({ row, indent }) {
  return (
    <>
      {indent && <span className="gantt-indent" />}
      <span
        className="gantt-stage-dot"
        style={{ background: row.color || "#94a3b8" }}
      />
      <div className="gantt-left-text">
        <div className="gantt-stage-name" title={row.stageName}>
          {row.stageName}
        </div>
        <div className="gantt-stage-meta">
          {row.assignees.length
            ? row.assignees.join(", ")
            : row.estimate
              ? `Est. ${row.estimate}`
              : "No assignee yet"}
        </div>
      </div>
      <span className={`gantt-state gantt-state-${row.state.toLowerCase()}`}>
        {STATE_LABELS[row.state] || row.state}
      </span>
    </>
  );
}

// options.keyPrefix keeps row keys unique when several projects are expanded;
// options.compact gives the denser rows used inside the portfolio.
export function buildStageViewRows(model, options = {}) {
  const { keyPrefix = "", compact = false, projectName = null } = options;
  const laneHeight = compact ? 20 : 26;
  const barHeight = compact ? 14 : 18;
  const minHeight = compact ? 38 : 48;

  const rows = model.rows.map((row) => ({
    key: `${keyPrefix}${row.stageId}`,
    height: Math.max(minHeight, row.lanes * laneHeight + (compact ? 8 : 14)),
    laneHeight,
    barHeight,
    lanes: row.lanes,
    className: compact ? "gantt-row-child" : "",
    label: <StageLabel row={row} indent={compact} />,
    bars: row.bars.map((bar) => ({
      id: `${keyPrefix}${bar.id}`,
      kind: bar.kind,
      start: bar.start,
      end: bar.end,
      lane: bar.lane,
      color: STATUS_COLORS[bar.status] || "#94a3b8",
      scheduled: bar.scheduledFuture,
      overdueFrom: bar.overdueMs > 0 ? bar.expectedEnd : null,
      label: bar.kind === "projected" ? row.stageName : bar.assignee,
      tooltip: (
        <StageBarTooltip bar={bar} row={row} projectName={projectName} />
      ),
    })),
  }));

  const barsByStage = new Map(model.rows.map((row) => [row.stageId, row.bars]));
  const dependencies = model.dependencies.map((dep) => {
    const fromEnd = Math.max(
      ...(barsByStage.get(dep.from) || []).map((b) => b.end),
    );
    const toStart = Math.min(
      ...(barsByStage.get(dep.to) || []).map((b) => b.start),
    );
    const late = Number.isFinite(fromEnd) && toStart < fromEnd;
    return {
      id: `${keyPrefix}${dep.id}`,
      from: `${keyPrefix}${dep.from}`,
      to: `${keyPrefix}${dep.to}`,
      tooltip: (
        <>
          {projectName && (
            <div className="gantt-tip-context">{projectName}</div>
          )}
          <div className="gantt-tip-title">Dependency</div>
          <div className="gantt-tip-dep">
            <span>{dep.fromName}</span>
            <span className="gantt-tip-arrow">→</span>
            <span>{dep.toName}</span>
          </div>
          <div className="gantt-tip-note">
            {late
              ? `${dep.toName} started before ${dep.fromName} finished.`
              : `${dep.toName} follows ${dep.fromName} in the workflow.`}
          </div>
        </>
      ),
    };
  });

  return { rows, dependencies };
}
